import { ChatToolSearchPeople } from "@/components/chat/chat-tool-search-people";
import { useWaitVariantIndex } from "@/hooks/useWaitVariantIndex";
import { useTranslation } from "@/hooks/useTranslation";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import {
  convertUIMessagePartsToText,
  isCompactionMessage,
  stripUnrecognizedShaarpTags,
} from "@/lib/ai";
import { getHistoryItemStyle } from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import hubspotIcon from "@/assets/integrations/hubspot.svg";
import seliarisIcon from "@/assets/integrations/seliaris.png";
import {
  ClarificationSkip,
  ConversationCompanyListToolOutput,
  ConversationMessageMetadata,
  ConversationMessagePromptInfo,
} from "@shaarp/common";
import { ChatStatus, ToolUIPart, UIMessage } from "ai";
import {
  AlertTriangleIcon,
  ArchiveIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleStopIcon,
  ClockIcon,
  CopyIcon,
  GlobeIcon,
  InfoIcon,
  Loader2Icon,
  PencilIcon,
  PlayIcon,
  RotateCcwIcon,
  SparklesIcon,
  XCircleIcon,
} from "lucide-react";
import {
  Fragment,
  memo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import TextareaAutosize from "react-textarea-autosize";
import AvatarCompany from "../company/avatar-company";
import { ChatSourcesRecap } from "./chat-sources-recap";
import ChatToolActivateTactic from "./chat-tool-activate-tactic";
import ChatToolProposeTactic from "./chat-tool-propose-tactic";
import ChatToolShowArtifact from "./chat-tool-show-artifact";
import FileCard from "./file-card";
import {
  CHAT_REQUEST_MODE_ICONS,
  CHAT_REQUEST_MODE_LABEL_KEYS,
} from "./chat-request-modes";
import {
  ContactRevealPreviewCard,
} from "./contact-reveal-card";
import {
  CHAT_MESSAGE_PART_GAP_CLASS,
  ChatAssistantMeta,
  ChatAssistantText,
  ChatStreamingIndicator,
  ChatUserMessageBubble,
} from "./chat-dialog-primitives";
import { ReasoningLiveLabel } from "@/components/ai-elements/reasoning";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { stripQuickReplyTags } from "./chat-quick-replies";
import { ChatClarificationPartCard } from "./chat-clarification-card";
import { CLARIFICATION_PART_TYPE } from "./chat-clarification-run";
import type { ClarificationReply } from "./chat-clarification-run";
import { ChatToolCompanyData } from "./chat-tool-company-data";
import { ChatCompanyListCard } from "./chat-company-list-card";
import { collectCompanyList } from "@/lib/company-list-parts";
import {
  buildAssistantRenderItems,
  lastItemBesideCompanyList,
  isStreamedPart,
  lastMeaningfulPartIndex,
  SEARCH_TOOL_TYPES,
  TRACE_TOOL_TYPES,
  type AssistantPart,
  streamWaitKind,
} from "@/lib/chat-render-items";
import { ChatToolCrmCompanies } from "./chat-tool-crm-companies";
import { ChatThinkingTrace } from "@/components/chat/chat-thinking-trace";
import { describeTurnWork } from "@/lib/chat-thinking-trace";


/**
 * Where a tool call ended up. A part that is neither done nor still running
 * belongs to a stream that stopped, which reads as a failure rather than as
 * something the user should keep waiting on.
 */
function ToolStatusIcon({
  isDone,
  isLive,
}: {
  isDone: boolean;
  isLive: boolean;
}) {
  if (isDone) {
    return <CheckCircleIcon className="size-4 text-green-600" />;
  }
  if (isLive) {
    return <ClockIcon className="size-4 animate-pulse" />;
  }
  return <XCircleIcon className="size-4 text-red-600" />;
}

/** The card of an answer that has produced nothing yet. */
function PendingThinkingTrace({
  messageId,
  answersClarification,
  turnStartedAtMs,
  headerLabelOverride,
}: {
  messageId: string;
  answersClarification: boolean;
  turnStartedAtMs?: number;
  headerLabelOverride?: string;
}) {
  return (
    <ChatThinkingTrace
      messageId={messageId}
      parts={[]}
      isLive
      isTrailing
      hasAnswerText={false}
      answersClarification={answersClarification}
      turnStartedAtMs={turnStartedAtMs}
      headerLabelOverride={headerLabelOverride}
    />
  );
}

// How long the answer may sit still before the dot says what it waits on: long
// enough not to flicker between two tokens, short enough not to read as a stall.
const STREAM_IDLE_MS = 1_500;

/**
 * Whether a live answer has stopped producing anything for a moment. The
 * signature changes with every token, so the timer restarts each time and only
 * fires on a real pause.
 */
function useIsStreamIdle(activitySignature: string, isActive: boolean): boolean {
  const [idleSignature, setIdleSignature] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(
      () => setIdleSignature(activitySignature),
      STREAM_IDLE_MS
    );
    return () => clearTimeout(timer);
  }, [activitySignature, isActive]);

  return isActive && idleSignature === activitySignature;
}

/**
 * The moment this message's live stream ended, `null` until it does — and
 * forever on a message that was already settled when it mounted.
 */
function useStreamEndedAt(streamInProgress: boolean): number | null {
  const [endedAt, setEndedAt] = useState<number | null>(null);
  const wasStreamingRef = useRef(false);

  useEffect(() => {
    if (streamInProgress) {
      wasStreamingRef.current = true;
      return;
    }
    if (wasStreamingRef.current) {
      wasStreamingRef.current = false;
      setEndedAt(Date.now());
    }
  }, [streamInProgress]);

  return endedAt;
}

/**
 * How long the turn took end to end — the question leaving the composer until
 * the answer landed, so reasoning, searches and writing together.
 *
 * Timed client-side: a live turn ends when its own stream does, and one read
 * back from history ends at the timestamp its answer was persisted with.
 */
function getTurnDurationMs(
  turnStartedAtMs: number | undefined,
  answeredAt: Date | string | undefined,
  streamEndedAt: number | null
): number | undefined {
  if (turnStartedAtMs === undefined) return undefined;

  const answeredAtMs =
    streamEndedAt ?? (answeredAt ? new Date(answeredAt).getTime() : undefined);
  if (answeredAtMs === undefined) return undefined;

  const durationMs = answeredAtMs - turnStartedAtMs;
  return durationMs > 0 ? durationMs : undefined;
}

type ChatMessageProps = {
  message: UIMessage<ConversationMessageMetadata>;
  /**
   * Whether this is the tail of the thread. Passed in as a primitive rather than
   * derived from the whole list, so a settled message keeps identical props
   * while a new answer streams in and is skipped by `memo`.
   */
  isLastMessage?: boolean;
  /**
   * When the question this answer replies to left the composer, so the turn can
   * be timed. A primitive for the same reason as `isLastMessage`: derived from
   * the thread here, a settled message would get new props on every delta.
   */
  turnStartedAtMs?: number;
  /**
   * Whether a continuation started here would land here. Distinct from
   * {@link isLastMessage} for one case: a failed turn renders an error card
   * after the answer, and the answer is still the message a resume appends to.
   */
  isResumableTail?: boolean;
  status: ChatStatus;
  streamInProgress?: boolean;
  /** Placeholder standing in for an answer whose stream hasn't started yet. */
  isPendingAnswer?: boolean;
  /** Names what a continuation is waiting on, shown next to the indicator. */
  streamStatusLabel?: string;
  onEditMessage?: (
    message: UIMessage<ConversationMessageMetadata>,
    newText: string
  ) => void;
  onRetryMessage?: () => void;
  onCompactConversation?: () => void;
  isCompacting?: boolean;
  /** Asks the assistant to pick up an interrupted answer where it stopped. */
  onResumeMessage?: () => void;
  /** Sends a reply to a clarifying question as an ordinary user message. */
  onAnswerQuestion?: (answer: string, skipped?: ClarificationSkip) => void;
  /**
   * The reply the clarifying question this message asks already got, resolved
   * from the thread: a message only ever sees its own parts.
   */
  questionReply?: ClarificationReply;
  /**
   * The turn answers the questions the previous one asked: its card opens on
   * taking those answers into account.
   */
  answersClarification?: boolean;
  /**
   * This turn worked, then asked a question, and the answer to the reply
   * carries its work on: its card shows both turns, and the footer is the
   * other answer's. Flat props rather than one object, so the memo holds.
   */
  isWorkContinued?: boolean;
  /** That answer, absent while it is awaited. */
  continuedBy?: UIMessage<ConversationMessageMetadata>;
  isContinuationLive?: boolean;
  continuationStartedAtMs?: number;
  /** The card of this turn's work is shown by the turn whose work it carries on. */
  isWorkShownAbove?: boolean;
  /**
   * What hangs off the end of this turn — the quick replies it offers, today.
   *
   * Rendered inside the message's own wrapper rather than after it, because the
   * meta footer is hung off that wrapper's bottom edge: left outside, the
   * trailer sits in the gap the footer is overlaid into and the two print on
   * top of each other. Inside, the footer lands below the trailer.
   */
  trailer?: ReactNode;
};

/**
 * The notice shown under an answer that did not run to its natural end.
 *
 * The *title and description* say what happened — a turn the app failed to
 * finish, or one that ran into how far a single turn may go. The *tone* is a
 * separate question, and it follows how much of the answer survived: a red
 * `failure` card over three readable pages tells the reader they lost work they
 * did not lose, so text that landed gets the softer `limit` card whatever ended
 * the turn. Both offer the same way out when the answer can be picked up.
 *
 * `STOPPED_BY_USER` deliberately stays out of it, on the pill above. A third
 * tone would render it here as a bordered card under a warning triangle, and a
 * stop is the one ending that is not an incident: the answer ends where it was
 * asked to, so it is acknowledged and nothing is offered. What separates the
 * two is not the tone but the shape — a card says something went wrong.
 */
function AnswerNoticeCard({
  tone,
  title,
  description,
  resumeLabel,
  onResume,
}: {
  tone: "failure" | "limit";
  title: string;
  description: string;
  /** Both this and `onResume` are needed for the button to be offered. */
  resumeLabel?: string;
  onResume?: () => void;
}) {
  const tones = {
    failure: {
      card: "border-destructive/30 bg-destructive/5",
      icon: "text-destructive",
    },
    limit: {
      card: "border-amber-500/40 bg-amber-500/5",
      icon: "text-amber-600",
    },
  } as const;

  return (
    <div
      role="status"
      className={cn(
        "flex flex-col gap-2.5 items-start p-3 mt-2 rounded-md border text-xs",
        tones[tone].card
      )}
    >
      <div className="flex gap-2 items-start">
        <AlertTriangleIcon
          className={cn("mt-0.5 size-3.5 shrink-0", tones[tone].icon)}
        />
        <div className="min-w-0">
          <p className="font-medium text-foreground">{title}</p>
          <p className="mt-0.5 text-muted-foreground">{description}</p>
        </div>
      </div>
      {resumeLabel && onResume && (
        <div className="pl-5.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={onResume}
          >
            <PlayIcon className="size-3.5" />
            {resumeLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

function ChatMessageBase({
  message,
  isLastMessage = false,
  turnStartedAtMs,
  isResumableTail,
  status,
  streamInProgress = false,
  isPendingAnswer = false,
  streamStatusLabel,
  onEditMessage,
  onRetryMessage,
  onCompactConversation,
  isCompacting = false,
  onResumeMessage,
  onAnswerQuestion,
  questionReply,
  answersClarification = false,
  isWorkContinued = false,
  continuedBy,
  isContinuationLive = false,
  continuationStartedAtMs,
  isWorkShownAbove = false,
  trailer,
}: ChatMessageProps) {
  const { t } = useTranslation();
  const streamEndedAt = useStreamEndedAt(streamInProgress);

  // Only the message being streamed can still have a tool running; on any other
  // message a pending tool part is the leftover of a stream that stopped.
  const isLive = status === "streaming" && isLastMessage;
  // Built once so a research group can be told whether it is the last item —
  // which is what decides, for a group holding no search, whether it is still
  // the wait the reader is watching or one the answer has already moved past.
  const renderItems = buildAssistantRenderItems(message.parts, { isLive });
  const trailingItem = lastItemBesideCompanyList(renderItems);
  // Live, a short line may only announce the next call — #426 moves it into
  // the card when the call shows up. Folding the card on it, then opening it
  // again a moment later, made it jump at every new step: it folds once the
  // answer is plainly under way. Its clock stops at the first word all the
  // same.
  // Only the first card started with the turn: a later one's timers would
  // count the work and the writing before it.
  const {
    firstGroup: firstResearchGroup,
    hasAnswerText,
    isAnswerUnderway: isAnswerUnderwayInThread,
    timeline: traceTimeline,
  } = describeTurnWork(message.parts, renderItems, isLive);
  // The answer that carried this turn's work on, read the same way: the card
  // shows its steps after this turn's, and follows its live state.
  const continuationParts = continuedBy?.parts ?? [];
  const continuationWork = isWorkContinued
    ? describeTurnWork(
        continuationParts,
        buildAssistantRenderItems(continuationParts, {
          isLive: isContinuationLive,
        }),
        isContinuationLive
      )
    : null;
  // Between the pending placeholder and the first part, the card stays put
  // rather than leaving a lone dot where the steps were.
  const isAwaitingFirstPart =
    isLive && lastMeaningfulPartIndex(message.parts) === -1;
  // Only the tail of the thread can be resumed: a continuation is appended at
  // the end, so offering it on an older interrupted answer would silently
  // answer somewhere other than where the button was clicked. An error card
  // sitting after the answer does not move that tail — see `isResumableTail`,
  // whose absence used to leave a failed continuation with no way back.
  const canResumeMessage =
    (isResumableTail ?? isLastMessage) &&
    !!onResumeMessage &&
    status !== "streaming" &&
    status !== "submitted";

  const messageType = message.metadata?.type;
  // How much of the answer survived, which is what the interrupted notice
  // reads to pick its tone.
  const messageText = convertUIMessagePartsToText(message);
  const isStreamIdle = useIsStreamIdle(
    `${message.parts.length}:${messageText.length}`,
    isLive
  );
  // What the stream dot is waiting on. A continuation names its own wait;
  // otherwise the answer says it whenever no card or text on screen moves.
  // The card waiting on the first part already names that wait.
  const waitKind =
    isLive && !isAwaitingFirstPart
      ? streamWaitKind(message.parts, { isIdle: isStreamIdle, trailingItem })
      : null;
  const waitVariantIndex = useWaitVariantIndex(waitKind);
  const waitLabel =
    streamStatusLabel ??
    (waitKind
      ? t(`chat.message.wait.${waitKind}.${waitVariantIndex}`)
      : undefined);

  // Hide compaction boundary messages — compaction is transparent to the user
  if (isCompactionMessage(message)) {
    return null;
  }

  if (message.role === "user") {
    return (
      <ChatUserMessage
        message={message}
        onEditMessage={onEditMessage}
        canEdit={status !== "streaming" && status !== "submitted"}
      />
    );
  }

  if (message.metadata?.error) {
    return (
      <ChatAssistantErrorMessage
        message={message}
        onRetryMessage={onRetryMessage}
        onCompactConversation={onCompactConversation}
        isCompacting={isCompacting}
      />
    );
  }

  /**
   * One part of the answer, as the thread draws it. The parts of a card of
   * work (CRM results, company data, tactics, people) are drawn with it too,
   * under its timeline.
   */
  const renderPart = (part: AssistantPart, i: number): ReactNode => {
      switch (part.type) {
        case "text": {
          const displayText = stripQuickReplyTags(part.text);
          return (
            <ChatAssistantText
              key={`${message.id}-${i}`}
              isStreaming={streamInProgress}
            >
              {displayText}
            </ChatAssistantText>
          );
        }
        case "tool-thinking":
          // The pending answer: the card opens before the first
          // chunk, and only the placeholder knows the turn's start.
          return (
            <PendingThinkingTrace
              key={`${message.id}-${i}`}
              messageId={message.id}
              answersClarification={answersClarification}
              turnStartedAtMs={
                isPendingAnswer ? turnStartedAtMs : undefined
              }
              headerLabelOverride={
                part.input === t("chat.conversation.compacting")
                  ? (part.input as string)
                  : undefined
              }
            />
          );
        // Reached only by a question the thread could not merge
        // into a run — one asked under a lead-in sentence, an
        // answer, or beside another tool call. Same card, a single
        // step long, carrying the reply the thread resolved for it.
        case CLARIFICATION_PART_TYPE:
          return (
            <ChatClarificationPartCard
              key={`${message.id}-${i}`}
              id={`${message.id}-${i}`}
              part={part as ToolUIPart}
              reply={questionReply}
              isTail={isLastMessage}
              disabled={status !== "ready"}
              onAnswer={onAnswerQuestion}
            />
          );
        case "tool-searchHubspotCompanies":
          return (
            <ChatToolCrmCompanies
              key={`${message.id}-${i}`}
              crmName="HubSpot"
              icon={hubspotIcon}
              state={part.state}
              output={part.output}
            />
          );
        case "tool-searchSeliarisCompanies":
          return (
            <ChatToolCrmCompanies
              key={`${message.id}-${i}`}
              crmName="Seliaris"
              icon={seliarisIcon}
              state={part.state}
              output={part.output}
            />
          );
        case "tool-getCompanyData":
          return (
            <ChatToolCompanyData
              key={`${message.id}-${i}`}
              state={part.state}
              output={part.output}
            />
          );
        case "tool-compactConversation": {
          const isComplete =
            part.state === "output-available";
          return (
            <div
              key={`${message.id}-${i}`}
              className="flex gap-2 items-center p-3 w-full rounded-xl border bg-background not-prose"
            >
              <SparklesIcon className="size-4 text-primary" />
              <ToolStatusIcon
                isDone={isComplete}
                isLive={isLive}
              />
              <span className="text-sm font-medium">
                {t("chat.message.tool.compactConversation")}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="size-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">
                    {t(
                      "chat.message.tool.compactConversationTooltip",
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        }
        // One line per activated tactic: an answer may apply
        // up to three, and the reader has to be able to tell
        // which ones shaped it.
        //
        // Per tactic, not per tool call. The server serves the same
        // slug again rather than refusing it — a retried round lost
        // the body from the model's context — and only deduplicates
        // when persisting, so the live stream carries the repeats.
        // Without this, the line appears up to three times while the
        // answer streams and once after a reload.
        case "tool-activateUserTactic": {
          const slug = (part.output as { slug?: string } | undefined)
            ?.slug;
          const isAlreadyNamed =
            !!slug &&
            message.parts.some(
              (earlier, j) =>
                j < i &&
                earlier.type === "tool-activateUserTactic" &&
                (earlier.output as { slug?: string } | undefined)
                  ?.slug === slug,
            );

          if (isAlreadyNamed) {
            return null;
          }

          return (
            <ChatToolActivateTactic
              key={`${message.id}-${i}`}
              state={part.state}
              output={part.output}
            />
          );
        }
        // The listing itself is the result — the assistant links to
        // it in its text — so this stays a one-line status only.
        case "tool-createConversationCompanyList": {
          // A list that could not be created still settles as a
          // normal output, so the state alone cannot be trusted to
          // claim it was saved.
          const output = part.output as
            | ConversationCompanyListToolOutput
            | undefined;
          const isSaved =
            part.state === "output-available" &&
            output?.success === true;
          const isFailed =
            part.state === "output-available" && !isSaved;
          return (
            <div
              key={`${message.id}-${i}`}
              className="flex gap-2 items-center w-full"
            >
              <ToolStatusIcon isDone={isSaved} isLive={isLive} />
              <span className="text-sm text-muted-foreground">
                {isSaved
                  ? t("chat.message.tool.conversationCompanyListDone")
                  : isFailed
                    ? t(
                        "chat.message.tool.conversationCompanyListFailed",
                      )
                    : isLive
                      ? t(
                          "chat.message.tool.conversationCompanyListPending",
                        )
                      : t(
                          "chat.message.tool.conversationCompanyListInterrupted",
                        )}
              </span>
            </div>
          );
        }
        // Contact-reveal and tactic-proposal cards render after the
        // assistant text (below), not inline where the tool call
        // streams in: they are cards to act on, read after the
        // sentence announcing them.
        case "tool-previewContactReveal":
        case "tool-proposeTactic":
          return null;
        // The people table itself is beside the conversation; the
        // thread keeps one line per search.
        case "tool-searchPeople":
          return (
            <ChatToolSearchPeople
              key={`${message.id}-${i}`}
              part={part as ToolUIPart}
              isLive={isLive}
              onSendMessage={onAnswerQuestion}
            />
          );
        case "tool-showArtifact":
        case "tool-editArtifact":
          return (
            <ChatToolShowArtifact
              key={`${message.id}-${i}`}
              kind={
                part.type === "tool-editArtifact" ? "edit" : "show"
              }
              state={part.state}
              input={part.input}
              output={part.output}
            />
          );
      }
  };

  return (
    <div className="flex relative flex-col justify-center w-full group">
      <div className="flex flex-1 gap-4 items-start">
        <div className="flex-1 min-w-0">
          {message.role === "assistant" ? (
            <div className="overflow-hidden w-full">
              <div className={CHAT_MESSAGE_PART_GAP_CLASS}>
                {isAwaitingFirstPart && !isWorkShownAbove && (
                  <PendingThinkingTrace
                    messageId={message.id}
                    answersClarification={answersClarification}
                    turnStartedAtMs={turnStartedAtMs}
                  />
                )}
                {renderItems.map((item) => {
                  if (item.kind === "companyList") {
                    return (
                      <ChatCompanyListCard
                        key={`${message.id}-company-list-${item.startIndex}`}
                        list={collectCompanyList(item.parts)}
                        isLive={isLive}
                      />
                    );
                  }
                  if (item.kind === "researchGroup") {
                    // A lone reasoning gets the card too: its words stay
                    // behind the step's detail like every other thought. The
                    // tools with a card of their own are a row of the
                    // timeline, and their card shows under it.
                    const isFirstGroup = item === firstResearchGroup;
                    const continues = isFirstGroup && continuationWork;
                    return (
                      <Fragment key={`${message.id}-group-${item.startIndex}`}>
                        {!(isFirstGroup && isWorkShownAbove) && (
                          <ChatThinkingTrace
                            messageId={message.id}
                            parts={item.parts}
                            isLive={continues ? isContinuationLive : isLive}
                            isTrailing={
                              continues
                                ? continuationWork.isTrailing
                                : item === trailingItem
                            }
                            hasAnswerText={
                              continues
                                ? continuationWork.hasAnswerText
                                : hasAnswerText
                            }
                            isAnswerUnderway={
                              continues
                                ? continuationWork.isAnswerUnderway
                                : isAnswerUnderwayInThread
                            }
                            answersClarification={
                              answersClarification && isFirstGroup
                            }
                            turnStartedAtMs={
                              continues
                                ? continuationStartedAtMs
                                : isFirstGroup
                                  ? turnStartedAtMs
                                  : undefined
                            }
                            // The timeline is the whole turn's: only the card it
                            // starts with can map its calls to rows.
                            timeline={isFirstGroup ? traceTimeline : null}
                            continuation={
                              continues
                                ? {
                                    parts: continuationWork.firstGroup?.parts ?? [],
                                    timeline: continuationWork.timeline,
                                  }
                                : undefined
                            }
                          />
                        )}
                        {item.parts.map(({ part, index }) =>
                          isStreamedPart(part) &&
                          TRACE_TOOL_TYPES.has(part.type) &&
                          !SEARCH_TOOL_TYPES.has(part.type)
                            ? renderPart(part, index)
                            : null
                        )}
                      </Fragment>
                    );
                  }
                  return renderPart(item.part, item.index);
                })}
                {/* Render contact-reveal cards after the text so the loading
                    card / result appears below the assistant's message rather
                    than above it (the tool call streams before the text). */}
                {message.parts.map((part, i) => {
                  if (part.type === "tool-previewContactReveal") {
                    return (
                      // Picking a homonym resolves against the API in place;
                      // only the follow-ups it offers once a value is revealed
                      // (a hook email, a call script) go through the thread.
                      <ContactRevealPreviewCard
                        key={`${message.id}-cr-${i}`}
                        part={part as ToolUIPart}
                        live={isLive}
                        onSendMessage={onAnswerQuestion}
                      />
                    );
                  }
                  if (part.type === "tool-proposeTactic") {
                    return (
                      <ChatToolProposeTactic
                        key={`${message.id}-pt-${i}`}
                        part={part as ToolUIPart}
                        live={isLive}
                      />
                    );
                  }
                  return null;
                })}
              </div>
              {/* Belongs to the text being written, so it hugs it instead of
                  taking the stack's gap. */}
              {streamInProgress && (
                <span className="inline-flex gap-2 items-center">
                  <ChatStreamingIndicator startedAtMs={turnStartedAtMs} />
                  {/* A pulsing dot with no reason beside it reads as a stall:
                      whenever nothing else moves, it says what it waits on. */}
                  {waitLabel && (
                    <span className="text-sm font-medium min-w-0">
                      <ReasoningLiveLabel label={waitLabel} />
                    </span>
                  )}
                </span>
              )}
              {/* A deliberate stop only needs acknowledging: the answer ends
                  where it was asked to. It gets the discreet pill, never the
                  resume button. */}
              {!streamInProgress && messageType === "STOPPED_BY_USER" && (
                <div className="inline-flex gap-1 items-center px-2 py-0.5 mt-1 text-xs rounded-full border text-muted-foreground border-border bg-muted">
                  <CircleStopIcon className="w-3 h-3" />
                  {t("chat.message.stopped")}
                </div>
              )}
              {/* A turn the app failed to finish (wall-clock deadline, provider
                  error, dropped connection) is the case worth a notice and a
                  way out.

                  The tone follows how much of the answer survived, not the type:
                  three pages under a red failure card read as lost work, when
                  most of what was asked for is right there and one click gets
                  the rest. Nothing written is the case that really did fail. */}
              {!streamInProgress && messageType === "INTERRUPTED" && (
                <AnswerNoticeCard
                  tone={messageText.trim().length > 0 ? "limit" : "failure"}
                  title={t("chat.message.interrupted.title")}
                  description={t("chat.message.interrupted.description")}
                  resumeLabel={
                    canResumeMessage
                      ? t("chat.message.interrupted.resume")
                      : undefined
                  }
                  onResume={onResumeMessage}
                />
              )}
              {/* The server resumed this answer as far as one turn is allowed
                  to go. Asking for more is a decision only the user can make —
                  it costs another turn — so it is offered, not taken. */}
              {!streamInProgress && messageType === "TRUNCATED" && (
                <AnswerNoticeCard
                  tone="limit"
                  title={t("chat.message.truncated.title")}
                  description={t("chat.message.truncated.description")}
                  resumeLabel={
                    canResumeMessage
                      ? t("chat.message.truncated.resume")
                      : undefined
                  }
                  onResume={onResumeMessage}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
      {trailer}
      {/* A sibling of the column, not a child of it: the footer is taken out
          of the flow and hung off the `relative` wrapper, and the column in
          between clips its overflow — which cuts a box positioned below it
          whatever its containing block is. */}
      {/* A turn carried on below ends with the answer that carried it on:
          one footer, under it. */}
      {message.role === "assistant" && !streamInProgress && !isWorkContinued && (
        <ChatAssistantMeta
          llmId={message.metadata?.llmId}
          createdAt={message.metadata?.createdAt}
          generationDurationMs={getTurnDurationMs(
            turnStartedAtMs,
            message.metadata?.createdAt,
            streamEndedAt
          )}
        >
          {/* The pages the answer cites, as a pill beside the model's: a line
              of its own between the answer and its quick replies piled one
              more row under every answer. */}
          <ChatSourcesRecap text={messageText} />
        </ChatAssistantMeta>
      )}
    </div>
  );
}

/**
 * A single chat message. Memoized because every stream delta re-renders the
 * whole thread: without it, each chunk re-parsed the markdown of all the already
 * settled messages, which is what made long conversations stall while typing an
 * answer.
 *
 * The default comparison, deliberately: every prop above is either a primitive
 * derived from the thread or an identity the thread keeps stable — the handlers
 * are memoized, and `questionReply` is cached on the message it was read from
 * (see `readReply`). Anything added here has to hold to that, or the memo stops
 * holding for the messages that carry it. `continuedBy` is a message too: it
 * changes while the answer it names streams — the card above must show that
 * work live — and holds once it settles.
 */
export const ChatMessage = memo(ChatMessageBase);

function ChatAssistantErrorMessage({
  message,
  onRetryMessage,
  onCompactConversation,
  isCompacting = false,
}: {
  message: UIMessage<ConversationMessageMetadata>;
  onRetryMessage?: () => void;
  onCompactConversation?: () => void;
  isCompacting?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const error = message.metadata?.error;
  if (!error) return null;

  // A known error code gets a translated, actionable explanation; the raw
  // server message (English, technical) is only a fallback for the rest.
  const codeKey = error.code ? `chat.message.error.codes.${error.code}` : null;
  const hasCodeTranslation = codeKey ? i18n.exists(`${codeKey}.title`) : false;
  const title = hasCodeTranslation
    ? t(`${codeKey}.title`)
    : t("chat.message.error.title");
  const description = hasCodeTranslation
    ? t(`${codeKey}.description`, {
        files: error.filenames?.join(", ") ?? "",
      })
    : error.message;
  // A full context is the one error the user can actually clear: compacting
  // summarizes the history behind a boundary and frees the window.
  const canCompact =
    error.code === "context_budget_exceeded" && !!onCompactConversation;

  return (
    <div className="flex relative flex-col justify-center w-full group">
      <div className="flex flex-1 gap-4 items-start">
        <div className="flex-1 min-w-0">
          <div
            role="alert"
            className="flex flex-col gap-3 p-4 w-full rounded-md border border-destructive/30 bg-destructive/5 text-sm"
          >
            <div className="flex gap-2 items-start">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="min-w-0">
                <p className="font-medium text-foreground">{title}</p>
                <p className="mt-1 break-words text-muted-foreground">
                  {description}
                </p>
              </div>
            </div>
            {error.cause && !hasCodeTranslation && (
              <div className="pl-6 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {t("chat.message.error.cause")}
                </span>{" "}
                <span className="break-words">{error.cause}</span>
              </div>
            )}
            {canCompact && (
              <div className="pl-6">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={isCompacting}
                  onClick={onCompactConversation}
                >
                  {isCompacting ? (
                    <Loader2Icon className="animate-spin size-3.5" />
                  ) : (
                    <ArchiveIcon className="size-3.5" />
                  )}
                  {isCompacting
                    ? t("chat.message.error.compacting")
                    : t("chat.message.error.compact")}
                </Button>
              </div>
            )}
            {error.retryable && onRetryMessage && (
              <div className="pl-6">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={onRetryMessage}
                >
                  <RotateCcwIcon className="size-3.5" />
                  {t("chat.message.error.retry")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatUserMessage({
  message,
  onEditMessage,
  canEdit,
}: {
  message: UIMessage<ConversationMessageMetadata>;
  onEditMessage?: (
    message: UIMessage<ConversationMessageMetadata>,
    newText: string
  ) => void;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const [, copy] = useCopyToClipboard();
  const [isEditing, setIsEditing] = useState(false);
  const [justCopied, setJustCopied] = useState(false);

  // Strip technical `[@shaarp/...]` markers (e.g. the contact id carried by a
  // "Je choisis ..." candidate selection) so the bubble — and what the user
  // copies or edits — stays human-readable.
  const text = stripUnrecognizedShaarpTags(
    convertUIMessagePartsToText(message),
  );
  const isPrompt = !!message.metadata?.prompt;
  const companyContext = message.metadata?.companyContext;
  const forcedTool = message.metadata?.forcedTool;
  const requestMode = message.metadata?.requestMode;
  const RequestModeIcon = requestMode
    ? CHAT_REQUEST_MODE_ICONS[requestMode]
    : null;
  const fileParts = message.parts.filter((p) => p.type === "file");
  const metadataFiles = message.metadata?.files ?? [];

  const handleCopy = async () => {
    const copied = await copy(text);
    if (!copied) return;
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 2000);
  };

  if (isEditing) {
    return (
      <div className="flex justify-end w-full group">
        <div className="flex flex-col gap-1.5 items-end w-full">
          <ChatMessageEditor
            initialText={text}
            onCancel={() => setIsEditing(false)}
            onSubmit={(newText) => {
              setIsEditing(false);
              onEditMessage?.(message, newText);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex relative justify-end w-full group">
      <div className="flex flex-col gap-1.5 items-end max-w-[80%]">
        {companyContext && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex items-center gap-1.5 rounded-full border bg-background py-0.5 pr-2.5 pl-0.5 text-xs text-muted-foreground">
                <AvatarCompany
                  company={companyContext}
                  className="border size-5 bg-muted"
                />
                <span className="max-w-[12rem] truncate">
                  {companyContext.name}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="left">
              {t("chat.message.companyContext", {
                companyName: companyContext.name,
              })}
            </TooltipContent>
          </Tooltip>
        )}
        {/* What the message was sent asking for, so the thread says it after
            the fact — on a reopened conversation too. */}
        {requestMode && RequestModeIcon && (
          <div className="inline-flex items-center gap-1.5 rounded-full border bg-background py-1 px-2.5 text-xs text-muted-foreground">
            <RequestModeIcon className="size-3.5 text-majorelle" />
            <span>{t(CHAT_REQUEST_MODE_LABEL_KEYS[requestMode])}</span>
          </div>
        )}
        {forcedTool === "webSearch" && (
          <div className="inline-flex items-center gap-1.5 rounded-full border bg-background py-1 px-2.5 text-xs text-muted-foreground">
            <GlobeIcon className="size-3.5 text-majorelle" />
            <span>{t("chat.message.webSearch")}</span>
          </div>
        )}
        {forcedTool === "hubspotCompanies" && (
          <div className="inline-flex items-center gap-1.5 rounded-full border bg-background py-1 px-2.5 text-xs text-muted-foreground">
            <img
              src={hubspotIcon}
              alt=""
              aria-hidden
              className="object-contain size-3.5"
            />
            <span>{t("chat.message.crmLookup", { crm: "HubSpot" })}</span>
          </div>
        )}
        {forcedTool === "seliarisCompanies" && (
          <div className="inline-flex items-center gap-1.5 rounded-full border bg-background py-1 px-2.5 text-xs text-muted-foreground">
            <img
              src={seliarisIcon}
              alt=""
              aria-hidden
              className="object-contain size-3.5"
            />
            <span>{t("chat.message.crmLookup", { crm: "Seliaris" })}</span>
          </div>
        )}
        {message.metadata?.prompt ? (
          <ChatMessagePromptPill prompt={message.metadata.prompt} />
        ) : (
          <ChatUserMessageBubble>{text}</ChatUserMessageBubble>
        )}
        {fileParts.length > 0 ? (
          <div className="flex overflow-x-auto p-1 space-x-2">
            {fileParts.map((file, index) => (
              <FileCard
                key={index}
                url={file.url}
                filename={file.filename || t("chat.message.attachmentFallbackName")}
              />
            ))}
          </div>
        ) : metadataFiles.length > 0 ? (
          <div className="flex overflow-x-auto p-1 space-x-2">
            {metadataFiles.map((file) => (
              <FileCard
                key={file.id}
                url={file.url}
                filename={file.name || t("chat.message.attachmentFallbackName")}
              />
            ))}
          </div>
        ) : null}
        {/* Overlaid into the gap below the bubble: reserved in the flow, it made
            the space under a question wider than anywhere else in the thread.
            Invisible means untouchable — without that it swallowed the clicks
            over a 28px band — and reachable by keyboard, where it was
            focusable and unlit. */}
        <div className="flex absolute -right-1 top-full items-center opacity-0 transition-opacity pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100 touch:pointer-events-auto touch:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground size-7 hover:text-foreground"
                onClick={handleCopy}
              >
                {justCopied ? (
                  <CheckIcon className="size-3.5" />
                ) : (
                  <CopyIcon className="size-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {justCopied ? t("chat.message.copied") : t("chat.message.copy")}
            </TooltipContent>
          </Tooltip>
          {!isPrompt && onEditMessage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground size-7 hover:text-foreground"
                  disabled={!canEdit}
                  onClick={() => setIsEditing(true)}
                >
                  <PencilIcon className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("chat.message.edit")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatMessageEditor({
  initialText,
  onCancel,
  onSubmit,
}: {
  initialText: string;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, []);

  const canSubmit = value.trim().length > 0;

  const submit = () => {
    if (canSubmit) onSubmit(value.trim());
  };

  return (
    <div className="p-3 w-full rounded-2xl border bg-background">
      <TextareaAutosize
        ref={textareaRef}
        value={value}
        minRows={1}
        maxRows={15}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
            return;
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        className="px-1 w-full text-base leading-relaxed bg-transparent border-0 shadow-none resize-none focus:outline-none"
      />
      <div className="flex gap-2 justify-end mt-2">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </Button>
        <Button
          size="sm"
          className="rounded-full"
          disabled={!canSubmit}
          onClick={submit}
        >
          {t("chat.input.send")}
        </Button>
      </div>
    </div>
  );
}

function ChatMessagePromptPill({
  prompt,
}: {
  prompt: ConversationMessagePromptInfo;
}) {
  const { Icon, colors } = getHistoryItemStyle(
    prompt.icon,
    prompt.categoryId,
    prompt.ownerType
  );

  return (
    <div className="inline-block px-4 py-2.5 leading-relaxed rounded-2xl rounded-br-sm bg-majorelle text-majorelle-foreground">
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium w-fit text-foreground",
          colors.iconBg
        )}
      >
        <Icon className={cn("size-3", colors.icon)} />
        <span className="truncate max-w-[200px]">{prompt.title}</span>
      </div>
      {prompt.userContent && (
        <div className="mt-2 whitespace-pre-line">{prompt.userContent}</div>
      )}
    </div>
  );
}
